import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import { ArrowLeft, Loader2, Settings, FileText, Palette, BarChart3, QrCode, Clock, MessageSquare, Grid, Link, Globe } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// Import components
import CategoryManager from '../../components/menu/CategoryManager';
import AllergenManager from '../../components/menu/AllergenManager';
import StyleEditor from '../../components/menu/StyleEditor';
import PDFGenerator from '../../components/menu/PDFGenerator';
import StatisticsDashboard from '../../components/menu/StatisticsDashboard';
import LiveMenuPanel from '../../components/menu/LiveMenuPanel';
import OpeningHoursEditor from '../../components/menu/OpeningHoursEditor';
import WhatsAppManager from '../../components/menu/WhatsAppManager';
import LanguageSelector from '../../components/menu/LanguageSelector';
import SpecialOpeningHoursManager from '../../components/menu/SpecialOpeningHoursManager';
import OpeningHoursEmbedEditor from '../../components/menu/OpeningHoursEmbedEditor';
import AIMenuImport from '../../components/menu/AIMenuImport';
import MenuSettings from '../../components/menu/MenuSettings';
import PDFManagement from '../../components/menu/PDFManagement';
import LiveView from '../../components/menu/LiveView';

interface Menu {
  id: number;
  title: string;
  description?: string;
  slug?: string;
  restaurantId?: number;
  restaurant?: {
    id: number;
    name: string;
    address?: string;
  };
  styleConfig?: any;
}
export interface Allergen {
  id: number;
  name: string;
  icon?: string;
}

/** Sort menu sections, categories, and items by position so reorder is reflected after refetch (backend may not apply order in nested includes). */
function sortMenuByPosition(menu: any): any {
  if (!menu) return menu;
  const sections = (menu.MenuSections || []).slice().sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
  sections.forEach((section: any) => {
    if (section.Categories) {
      section.Categories = section.Categories.slice().sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
      section.Categories.forEach((cat: any) => {
        if (cat.MenuItems) {
          cat.MenuItems = cat.MenuItems.slice().sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
        }
      });
    }
  });
  return { ...menu, MenuSections: sections };
}

const MenuDetailPage = () => {
  const { menuId } = useParams<{ menuId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [menu, setMenu] = useState<Menu | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('categories');
  const [allergens, setAllergens] = useState<Allergen[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState('it');

  useEffect(() => {
    if (menuId) {
      fetchMenuData();
    }
  }, [menuId]);

  const fetchMenuData = async () => {
    if (!menuId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/menu/${menuId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setMenu(sortMenuByPosition(response.data.data));
      }
    } catch (error: any) {
      console.error('Failed to fetch menu:', error);
      toast.error(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAllergensUpdate = (updatedAllergens: any[]) => {
    setAllergens(updatedAllergens);
  };

  const tabs = [
    { id: 'categories', label:'Categorie e Piatti', icon: Grid },
    { id: 'ai-import', label: 'Importazione Menu AI', icon: FileText },
    { id: 'settings', label: 'Impostazioni Menu', icon: Settings },
    { id: 'allergens', label: 'Allergeni', icon: Settings },
    { id: 'pdf', label: 'Gestione PDF', icon: FileText },
    { id: 'live', label: 'Visualizzazione Live', icon: Link },
    { id: 'hours', label: 'Orari di Apertura', icon: Clock },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
    { id: 'statistics', label: 'Statistiche', icon: BarChart3 }
  ];

  const [hoursSubTab, setHoursSubTab] = useState<'regular' | 'special' | 'embed'>('regular');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#7BD5B5]" />
      </div>
    );
  }

  if (!menu) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800">{t('menu_detail.menu_not_found')}</p>
          <button
            onClick={() => navigate('/menus')}
            className="mt-4 px-4 py-2 bg-[#0A0C0B] text-white rounded-lg"
          >
            {t('menu_detail.back_to_menus')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={() => navigate('/menus')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-[#0A0C0B] truncate">
                  {menu.title}
                </h1>
                {menu.restaurant && (
                  <p className="text-xs sm:text-sm text-[#687d76] truncate">
                    {menu.restaurant.name}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation - Mobile Optimized */}
      <div className="bg-white border-b border-gray-200 sticky top-[73px] z-10">
        <div className="px-4 sm:px-6">
          {/* Mobile Tabs - Scrollable */}
          <div className="sm:hidden overflow-x-auto py-3 -mx-4 px-4">
            <div className="flex space-x-1 min-w-max w-full">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 text-xs font-medium rounded-lg transition-all whitespace-nowrap flex items-center gap-1.5 min-w-[100px] ${
                    activeTab === tab.id
                      ? 'bg-[#0A0C0B] text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5 flex-shrink-0 text-[#7BD5B5]" />
                  <span className="truncate">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Desktop Tabs - Normal */}
          <nav className="hidden sm:flex space-x-1 overflow-x-auto py-3">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-[#0A0C0B] text-white shadow-sm'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <tab.icon className="w-4 h-4 text-[#7BD5B5]" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content - Mobile Optimized */}
      <div className="px-4 sm:px-6 py-6">
        {activeTab === 'settings' && menuId && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <MenuSettings
              menuId={menuId}
              onUpdate={fetchMenuData}
            />
          </div>
        )}

        {activeTab === 'pdf' && menuId && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <PDFManagement
              menuId={menuId}
              menuTitle={menu.title}
              currentLanguage={selectedLanguage}
            />
          </div>
        )}

        {activeTab === 'live' && menuId && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <LiveView
              menuId={menuId}
              menuSlug={menu.slug || menuId}
              menuTitle={menu.title}
            />
          </div>
        )}

        {activeTab === 'categories' && menuId && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <CategoryManager
              menu={menu}
              menuId={menuId}
              allergens={allergens}
              onUpdate={fetchMenuData}
              selectedLanguage={selectedLanguage}
            />
          </div>
        )}

        {activeTab === 'ai-import' && menuId && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <AIMenuImport menuId={menuId} onImportComplete={() => {
              // Delay so the success toast has time to show before the page re-renders
              setTimeout(() => {
                fetchMenuData();
                setActiveTab('categories');
              }, 2000);
            }} />
          </div>
        )}

        {activeTab === 'allergens' && menuId && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <AllergenManager
              menuId={menuId}
              allergens={allergens as unknown as never[]}
              onAllergensUpdate={handleAllergensUpdate}
            />
          </div>
        )}

        {activeTab === 'hours' && menuId && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <div className="flex flex-wrap gap-1 sm:gap-2 border-b border-gray-200 pb-3 mb-4">
              <button
                onClick={() => setHoursSubTab('regular')}
                className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                  hoursSubTab === 'regular'
                    ? 'bg-[#0A0C0B] text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
               Orari Normali
              </button>
              <button
                onClick={() => setHoursSubTab('special')}
                className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                  hoursSubTab === 'special'
                    ? 'bg-[#0A0C0B] text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
             
                   Orari Speciali
              </button>
              <button
                onClick={() => setHoursSubTab('embed')}
                className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                  hoursSubTab === 'embed'
                    ? 'bg-[#0A0C0B] text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
               Codice di Incorporamento
              </button>
            </div>

            {hoursSubTab === 'regular' && (
              <OpeningHoursEditor menuId={menuId} restaurantId={menu.restaurantId} />
            )}

            {hoursSubTab === 'special' && (
              <SpecialOpeningHoursManager menuId={menuId} restaurantId={menu.restaurantId} />
            )}

            {hoursSubTab === 'embed' && (
              <OpeningHoursEmbedEditor menuId={menuId} restaurantId={menu.restaurantId} />
            )}
          </div>
        )}

        {activeTab === 'whatsapp' && menuId && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <WhatsAppManager menuId={menuId} />
          </div>
        )}

        {activeTab === 'statistics' && menuId && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
            <StatisticsDashboard menuId={menuId} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuDetailPage;